// Zclub QR Scan API Service
// Based on ZCLUB_QR_SCAN_API_DOCUMENTATION.md
import { makeAuthenticatedApiCall } from './authApi';
import { ZCLUB_API_URL, API_BASE_URL } from '../config/api';
import { getPaygiftClaimModalUi } from '../utils/paygiftUserFacingMessage';

const API_BASE_URL_SCAN = `${ZCLUB_API_URL}/`;

// Validate QR scan response format
const validateQrScanResponse = (response) => {
  if (!response) {
    throw new Error('Empty response received');
  }
  
  if (response.code !== 200 || response.status !== 'success') {
    throw new Error(response.message || 'QR scan failed');
  }
  
  if (!response.data) {
    throw new Error('No data in response');
  }
  
  const { qr_code, rewards, user_balance } = response.data;
  
  if (!qr_code || !rewards || !user_balance) {
    throw new Error('Invalid response format: missing required data fields');
  }
  
  // Validate coupon data if present (optional field)
  if (rewards.kupon_diklaim) {
    const coupon = rewards.kupon_diklaim;
    const isDigitalPending =
      coupon.tipe_kupon === 'digiprosb'
      && (coupon.reward_pending === true || coupon.voucher_details?.pending === true);
    // Digiprosb pending has no SN yet — kode_kupon may be null
    if (!coupon.id || !coupon.nama_kupon || (!coupon.kode_kupon && !isDigitalPending)) {
      throw new Error('Invalid coupon data format');
    }
  }
  
  return true;
};

// Scan QR Code
// Supports both path parameter and body parameter formats as per API documentation
export const scanQrCode = async (qrCode, usePathParameter = false) => {
  try {
    // console.log('scanQrCode called with:', qrCode, 'usePathParameter:', usePathParameter);
    console.log('scanQrCode called');
    
    let url = `${API_BASE_URL_SCAN}scan-qr`;
    let requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    };
    
    if (usePathParameter) {
      // Use path parameter format: POST /scan-qr/{qrCode}
      url = `${API_BASE_URL_SCAN}scan-qr/${encodeURIComponent(qrCode)}`;
    } else {
      // Use body parameter format with support for both field names
      requestOptions.body = JSON.stringify({
        qr_code: qrCode,
        kode_qr: qrCode  // Alternative field name as per documentation
      });
    }
    
    const response = await makeAuthenticatedApiCall(url, requestOptions);

    // console.log('scanQrCode response:', response);
    
    // Validate response format
    validateQrScanResponse(response);
    
    return response;
  } catch (error) {
    console.error('scanQrCode error:', error);
    throw error;
  }
};

// Get User Scan History
export const getUserScanHistory = async (limit = 20) => {
  try {
    console.log('getUserScanHistory called with limit:', limit);
    
    const response = await makeAuthenticatedApiCall(`${API_BASE_URL_SCAN}scan-history?limit=${limit}`, {
      method: 'GET'
    });

    // console.log('getUserScanHistory response:', response);
    return response;
  } catch (error) {
    console.error('getUserScanHistory error:', error);
    throw error;
  }
};

// Test API connectivity
export const testQrScanApi = async () => {
  try {
    console.log('testQrScanApi called');
    
    const response = await fetch(`${API_BASE_URL_SCAN}test`, {
      method: 'GET'
    });

    const result = await response.json();
    // console.log('testQrScanApi response:', result);
    return result;
  } catch (error) {
    console.error('testQrScanApi error:', error);
    throw error;
  }
};

// Validate QR Code (without claiming rewards)
// This endpoint provides QR code information without actually scanning/claiming rewards
export const validateQrCode = async (qrCode, usePathParameter = false) => {
  try {
    console.log('validateQrCode called');
    // console.log('validateQrCode called with:', qrCode, 'usePathParameter:', usePathParameter);
    
    let url = `${API_BASE_URL_SCAN}qr-scan/validate`;
    let requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    };
    
    if (usePathParameter) {
      // Use path parameter format: POST /qr-scan/validate/{qrCode}
      url = `${API_BASE_URL_SCAN}qr-scan/validate/${encodeURIComponent(qrCode)}`;
    } else {
      // Use body parameter format with support for both field names
      requestOptions.body = JSON.stringify({
        qr_code: qrCode,
        kode_qr: qrCode  // Alternative field name as per documentation
      });
    }
    
    const response = await makeAuthenticatedApiCall(url, requestOptions);

    // console.log('validateQrCode response:', response);
    
    // Validate response format
    if (!response) {
      throw new Error('Empty response received');
    }
    
    if (response.code !== 200 || response.status !== 'success') {
      throw new Error(response.message || 'QR validation failed');
    }
    
    if (!response.data) {
      throw new Error('No data in response');
    }
    
    return response;
  } catch (error) {
    console.error('validateQrCode error:', error);
    throw error;
  }
};

// Transform scan result for UI display
export const transformScanResult = (apiResponse) => {
  if (!apiResponse || !apiResponse.data) {
    return null;
  }

  const { data } = apiResponse;
  const { qr_code, rewards, user_balance } = data;

  // Parse rewards values (handle both string and number formats)
  const pointsEarned = parseInt(rewards.poin_diberikan) || 0;
  const stampsEarned = parseInt(rewards.estamp_diberikan) || 0;
  const couponClaimed = rewards.kupon_diklaim || null;

  // Determine scan type based on rewards
  let type = 'points';
  let icon = 'star';
  let title = 'Scan Successful!';
  let message = apiResponse.message || 'QR Code scanned successfully';

  // Handle all possible reward combinations using switch
  const rewardKey = `${couponClaimed ? 'C' : ''}${stampsEarned > 0 ? 'S' : ''}${pointsEarned > 0 ? 'P' : ''}`;
  
  switch (rewardKey) {
    case 'CSP': // Coupon + Stamps + Points
      type = 'all';
      icon = 'gift';
      title = 'Amazing! All Rewards Earned!';
      break;
    case 'CS': // Coupon + Stamps
      type = 'coupon_stamp';
      icon = 'gift-outline';
      title = 'Coupon & E-Stamp Earned!';
      break;
    case 'CP': // Coupon + Points
      type = 'coupon_points';
      icon = 'gift-outline';
      title = 'Coupon & Points Earned!';
      break;
    case 'C': // Coupon only
      type = 'coupon';
      icon = 'gift-outline';
      title = 'Reward Berhasil Diklaim!';
      break;
    case 'SP': // Stamps + Points
      // type = 'both';
      type = 'points_stamp';
      icon = 'gift';
      title = 'Rewards Earned!';
      break;
    case 'S': // Stamps only
      type = 'stamp';
      icon = 'bookmark';
      title = 'E-Stamp Collected!';
      break;
    case 'P': // Points only
      type = 'points';
      icon = 'star';
      title = 'Points Earned!';
      break;
    default:
      // No rewards case handled by initial values
      break;
  }

  const batchRaw = data.zclub_qr_batch_id;
  const zclubQrBatchId =
    batchRaw !== undefined && batchRaw !== null ? parseInt(String(batchRaw), 10) : 0;

  const result = {
    type,
    title,
    message,
    icon,
    color: '#222',
    zclub_qr_batch_id: Number.isFinite(zclubQrBatchId) ? zclubQrBatchId : 0,
    qrCode: {
      id: qr_code.id,
      nama: qr_code.nama,
      deskripsi: qr_code.deskripsi,
      brand: {
        nama: qr_code.brand,
        logo: qr_code.logo_url
      }
    },
    rewards: {
      points: pointsEarned,
      stamps: stampsEarned
    },
    userBalance: {
      totalPoints: parseInt(user_balance.total_poin) || 0,
      totalStamps: parseInt(user_balance.total_estamp) || 0
    },
    scanTime: data.scan_time,
    newSessionId: apiResponse.new_session_id
  };

  // Add coupon information if claimed - include full details for CouponDetailsScreen
  if (couponClaimed) {
    const isIndobest = couponClaimed.tipe_kupon === 'indobest';
    const isPaygift = couponClaimed.tipe_kupon === 'paygift';
    const isDigitalReward = couponClaimed.tipe_kupon === 'digiprosb';
    const voucherDetails = couponClaimed.voucher_details || {};
    const pendingReason =
      couponClaimed.paygift_pending_reason
      || voucherDetails.paygift_pending_reason
      || null;
    const voucherUrl =
      couponClaimed.voucher_url
      || voucherDetails.voucher_url
      || null;
    const voucherExpiryDate =
      couponClaimed.voucher_expiry_date
      || voucherDetails.voucher_expiry_date
      || null;
    const rewardSnRaw = voucherDetails.sn || couponClaimed.reward_sn || null;
    const rewardSnReady = (() => {
      if (!rewardSnRaw) return false;
      const s = String(rewardSnRaw).trim();
      return !!s && s !== 'PENDING' && !s.startsWith('PENDING_');
    })();
    const rewardSn = rewardSnReady ? String(rewardSnRaw).trim() : rewardSnRaw;
    const rewardAwaitingPhone = isDigitalReward && !rewardSnReady
      ? !!(couponClaimed.reward_awaiting_phone ?? voucherDetails.awaiting_phone)
      : false;
    const rewardFailed = isDigitalReward && !rewardSnReady
      ? !!(couponClaimed.reward_failed ?? voucherDetails.failed)
      : false;
    const rewardProcessing = isDigitalReward && !rewardSnReady
      ? !!(couponClaimed.reward_processing ?? voucherDetails.processing)
      : false;
    const rewardPending = isDigitalReward && !rewardSnReady
      ? !!(
          couponClaimed.reward_pending
          ?? voucherDetails.pending
          ?? (rewardAwaitingPhone || rewardProcessing || rewardFailed || !rewardSn)
        )
      : false;
    const displayCode = isDigitalReward
      ? (rewardPending ? null : (rewardSn || couponClaimed.kode_kupon || null))
      : couponClaimed.kode_kupon;

    result.coupon = {
      id: couponClaimed.id,
      claim_id: couponClaimed.claim_id,
      claimId: couponClaimed.claim_id,
      kode_kupon: displayCode,
      code: displayCode,
      coupon_code: displayCode,
      nama_kupon: couponClaimed.nama_kupon,
      title: couponClaimed.nama_kupon,
      tipe_kupon: couponClaimed.tipe_kupon,
      type: couponClaimed.tipe_kupon,
      nilai_diskon: couponClaimed.nilai_diskon,
      tanggal_berakhir: couponClaimed.tanggal_berakhir,
      endDate: couponClaimed.tanggal_berakhir,
      deskripsi: couponClaimed.deskripsi,
      description: couponClaimed.deskripsi,
      syarat_ketentuan: couponClaimed.syarat_ketentuan,
      termsConditions: couponClaimed.syarat_ketentuan,
      image: couponClaimed.gambar_url ? { uri: couponClaimed.gambar_url } : (couponClaimed.gambar_kupon ? { uri: `${API_BASE_URL}/uploads/kupon/${couponClaimed.gambar_kupon}` } : null),
      is_claimed: couponClaimed.is_claimed !== false,
      isIndobest,
      isPaygift,
      paygift: isPaygift,
      indobest: isIndobest,
      voucher_url: voucherUrl,
      voucher_expiry_date: voucherExpiryDate,
      paygift_pending_reason: pendingReason,
      paygift_trx_id:
        couponClaimed.paygift_transaction_id
        || voucherDetails.paygift_transaction_id
        || couponClaimed.paygift_trx_id
        || null,
      external_ref_id:
        couponClaimed.external_ref_id
        || voucherDetails.external_ref_id
        || null,
      voucher_details: voucherDetails,
      reward_pending: rewardPending,
      reward_awaiting_phone: rewardAwaitingPhone,
      reward_processing: rewardProcessing,
      reward_failed: rewardFailed,
      reward_failure_message: couponClaimed.reward_failure_message || voucherDetails.failure_message || null,
      reward_sn: rewardSn,
      suggested_phone: couponClaimed.suggested_phone || null,
      tampil_barcode: !!(couponClaimed.tampil_barcode || voucherDetails.tampil_barcode),
      digiprosb_dest_type: couponClaimed.digiprosb_dest_type || voucherDetails.dest_type || null,
      dest_form: couponClaimed.dest_form || null,
    };
    if (data.kawan_activation) {
      result.coupon.kawan_activation = data.kawan_activation;
    }

    // Klarifikasi status Paygift di modal sukses (bukan "dibatalkan")
    if (isPaygift) {
      const voucherName =
        couponClaimed.nama_kupon
        || voucherDetails.nama_kupon
        || 'Voucher';
      const isQueuedPending =
        pendingReason === 'quantity_shortage'
        || pendingReason === 'not_available_sales_product'
        || pendingReason === 'not_sales_period'
        || pendingReason === 'api_down_maintenance'
        || pendingReason === 'insufficient_deposit'
        || pendingReason === 'duplicated_purchase'
        || !voucherUrl;

      if (isQueuedPending) {
        const modalUi = getPaygiftClaimModalUi({
          paygift: true,
          tipe_kupon: 'paygift',
          paygift_pending_reason: pendingReason,
          voucher_url: voucherUrl,
        });
        result.title = modalUi.title;
        result.message = modalUi.headerMessage;
        result.paygiftClaimPending = true;
        result.paygiftClaimModalUi = modalUi;
      } else {
        result.title = 'Reward Berhasil Diklaim!';
        result.message = apiResponse.message || `${voucherName} siap digunakan.`;
        result.paygiftClaimPending = false;
      }
    }

    if (isDigitalReward && rewardAwaitingPhone) {
      result.title = 'Kupon tersimpan';
      result.message =
        apiResponse.message
        || 'Buka detail kupon dan masukkan data tujuan sesuai deskripsi untuk memproses voucher.';
      result.digitalRewardAwaitingPhone = true;
      result.digitalRewardPending = true;
    } else if (isDigitalReward && rewardPending) {
      result.title = 'Voucher sedang diproses';
      result.message =
        apiResponse.message
        || 'Voucher sedang diproses. Cek menu Kupon sebentar lagi.';
      result.digitalRewardPending = true;
    }
  }

  return result;
};

// Transform validate QR response for UI display
export const transformValidateResult = (apiResponse) => {
  if (!apiResponse || !apiResponse.data) {
    return null;
  }

  const { data } = apiResponse;
  const { qr_code, rewards, coupon_details, valid_until, max_scans, remaining_scans } = data;

  // Parse rewards values
  const pointsGiven = parseInt(rewards.poin_diberikan) || 0;
  const stampsGiven = parseInt(rewards.estamp_diberikan) || 0;
  const hasCoupon = rewards.kupon_tersedia || false;

  const result = {
    type: 'validation',
    title: 'Selamat',
    message: apiResponse.message || 'QR Code is valid and ready to scan',
    icon: 'checkmark-circle',
    color: '#4CAF50',
    qrCode: {
      id: qr_code.id,
      nama: qr_code.nama,
      deskripsi: qr_code.deskripsi,
      brand: {
        nama: qr_code.brand,
        logo: qr_code.logo_url
      }
    },
    rewards: {
      points: pointsGiven,
      stamps: stampsGiven,
      hasCoupon: hasCoupon
    },
    couponDetails: coupon_details ? {
      id: coupon_details.id,
      nama_kupon: coupon_details.nama_kupon,
      tipe_kupon: coupon_details.tipe_kupon,
      nilai_diskon: coupon_details.nilai_diskon,
      deskripsi: coupon_details.deskripsi,
      tanggal_berakhir: coupon_details.tanggal_berakhir,
      syarat_ketentuan: coupon_details.syarat_ketentuan,
      image: coupon_details.gambar_url ? { uri: coupon_details.gambar_url } : (coupon_details.gambar_kupon ? { uri: `${API_BASE_URL}/uploads/kupon/${coupon_details.gambar_kupon}` } : null)
    } : null,
    validity: {
      validUntil: valid_until,
      maxScans: max_scans,
      remainingScans: remaining_scans
    }
  };

  return result;
};

// Transform scan history for UI display
export const transformScanHistory = (apiResponse) => {
  if (!apiResponse || !apiResponse.data || !apiResponse.data.scan_history) {
    return [];
  }

  return apiResponse.data.scan_history.map(item => ({
    id: item.id,
    qrCode: {
      name: item.qr_code.nama,
      code: item.qr_code.kode
    },
    brand: item.brand,
    rewards: {
      points: item.rewards.poin || 0,
      stamps: item.rewards.estamp || 0
    },
    status: item.status,
    notes: item.catatan,
    scanTime: item.scan_time,
    scanDate: item.scan_date
  }));
};